import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { theme } from '../../theme/theme';
import { CommentItem } from './CommentItem';
import { CommentInput } from './CommentInput';

interface User {
  id: string;
  name: string;
  username: string;
  avatarUrl?: string;
}

interface Reply {
  id: string;
  content: string;
  user: User;
  likesCount: number;
  createdAt: string;
  isEdited: boolean;
  editedAt?: string;
  isLiked?: boolean;
}

interface Comment {
  id: string;
  content: string;
  user: User;
  likesCount: number;
  repliesCount: number;
  createdAt: string;
  isEdited: boolean;
  editedAt?: string;
  replies?: Reply[];
  isLiked?: boolean;
}

interface CommentsListProps {
  artworkId: string;
  currentUser?: User;
  onUserPress: (userId: string) => void;
}

export const CommentsList: React.FC<CommentsListProps> = ({
  artworkId,
  currentUser,
  onUserPress,
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<'created_at' | 'likes_count'>('created_at');
  const [replyTo, setReplyTo] = useState<{ id: string; user: User } | null>(null);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);

  const loadComments = useCallback(async (
    pageNum: number = 1,
    append: boolean = false,
    currentSortBy: 'created_at' | 'likes_count' = sortBy
  ) => {
    if (pageNum === 1) {
      setLoading(true);
    }

    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '20',
        sortBy: currentSortBy,
        sortOrder: 'DESC',
      });

      const response = await fetch(`http://localhost:3001/comments/artwork/${artworkId}?${params}`, {
        headers: {
          // TODO: Add authorization header
          // 'Authorization': `Bearer ${userToken}`,
        },
      });

      const result = await response.json();

      if (response.ok) {
        const newComments = result.comments || [];
        setComments(prev => append ? [...prev, ...newComments] : newComments);
        setTotal(result.total || 0);
        setHasMore(newComments.length === 20);
        setPage(pageNum);
      } else {
        console.error('Load comments error:', result.message);
      }
    } catch (error) {
      console.error('Load comments request error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [artworkId, sortBy]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleRefresh = () => {
    setRefreshing(true);
    setPage(1);
    loadComments(1, false);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      loadComments(nextPage, true);
    }
  };

  const handleSortChange = (newSortBy: 'created_at' | 'likes_count') => {
    setSortBy(newSortBy);
    setPage(1);
    loadComments(1, false, newSortBy);
  };

  const handleSubmitComment = async (content: string, parentId?: string) => {
    if (!currentUser) {
      Alert.alert('提示', '请先登录后再评论');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('http://localhost:3001/comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // TODO: Add authorization header
          // 'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          content,
          artworkId,
          parentId,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // 刷新评论列表
        handleRefresh();
        setReplyTo(null);
        setEditingComment(null);
      } else {
        Alert.alert('评论失败', result.message || '发布评论失败，请重试');
      }
    } catch (error) {
      console.error('Submit comment error:', error);
      Alert.alert('评论失败', '网络错误，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeComment = async (commentId: string) => {
    if (!currentUser) {
      Alert.alert('提示', '请先登录后再点赞');
      return;
    }

    try {
      const response = await fetch(`http://localhost:3001/comments/${commentId}/like`, {
        method: 'POST',
        headers: {
          // TODO: Add authorization header
          // 'Authorization': `Bearer ${userToken}`,
        },
      });

      const result = await response.json();

      if (response.ok) {
        // 更新本地状态
        setComments(prev => prev.map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              isLiked: result.isLiked,
              likesCount: result.likesCount,
            };
          }
          
          // 检查回复
          if (comment.replies) {
            const updatedReplies = comment.replies.map(reply => {
              if (reply.id === commentId) {
                return {
                  ...reply,
                  isLiked: result.isLiked,
                  likesCount: result.likesCount,
                };
              }
              return reply;
            });
            
            if (updatedReplies !== comment.replies) {
              return { ...comment, replies: updatedReplies };
            }
          }
          
          return comment;
        }));
      } else {
        Alert.alert('操作失败', result.message || '点赞失败，请重试');
      }
    } catch (error) {
      console.error('Like comment error:', error);
      Alert.alert('操作失败', '网络错误，请重试');
    }
  };

  const handleReplyComment = (comment: Comment) => {
    setReplyTo({ id: comment.id, user: comment.user });
    setEditingComment(null);
  };

  const handleEditComment = (comment: Comment) => {
    setEditingComment(comment);
    setReplyTo(null);
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      const response = await fetch(`http://localhost:3001/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          // TODO: Add authorization header
          // 'Authorization': `Bearer ${userToken}`,
        },
      });

      const result = await response.json();

      if (response.ok) {
        // 刷新评论列表
        handleRefresh();
      } else {
        Alert.alert('删除失败', result.message || '删除评论失败，请重试');
      }
    } catch (error) {
      console.error('Delete comment error:', error);
      Alert.alert('删除失败', '网络错误，请重试');
    }
  };

  const handleCancelReply = () => {
    setReplyTo(null);
    setEditingComment(null);
  };

  const renderComment = ({ item }: { item: Comment }) => (
    <CommentItem
      comment={item}
      currentUserId={currentUser?.id}
      onLike={handleLikeComment}
      onReply={handleReplyComment}
      onEdit={handleEditComment}
      onDelete={handleDeleteComment}
      onUserPress={onUserPress}
    />
  );

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerTop}>
        <Text style={styles.title}>评论 ({total})</Text>
        
        <View style={styles.sortButtons}>
          <TouchableOpacity
            style={[
              styles.sortButton,
              sortBy === 'created_at' && styles.sortButtonActive,
            ]}
            onPress={() => handleSortChange('created_at')}
          >
            <Text style={[
              styles.sortButtonText,
              sortBy === 'created_at' && styles.sortButtonTextActive,
            ]}>
              最新
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[
              styles.sortButton,
              sortBy === 'likes_count' && styles.sortButtonActive,
            ]}
            onPress={() => handleSortChange('likes_count')}
          >
            <Text style={[
              styles.sortButtonText,
              sortBy === 'likes_count' && styles.sortButtonTextActive,
            ]}>
              热门
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyTitle}>还没有评论</Text>
      <Text style={styles.emptyText}>成为第一个评论的人吧</Text>
    </View>
  );

  const renderFooter = () => {
    if (!loading || page === 1) return null;
    
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator color={theme.colors.primary} />
        <Text style={styles.loadingText}>加载更多...</Text>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <FlatList
        data={comments}
        renderItem={renderComment}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={!loading ? renderEmpty : null}
        ListFooterComponent={renderFooter}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[theme.colors.primary]}
          />
        }
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.1}
        showsVerticalScrollIndicator={false}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
      />

      {loading && page === 1 && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      )}

      {/* 评论输入框 */}
      <CommentInput
        currentUser={currentUser}
        placeholder={
          editingComment 
            ? '编辑评论...' 
            : replyTo 
              ? `回复 @${replyTo.user.name}...` 
              : '写下你的评论...'
        }
        replyTo={replyTo}
        onSubmit={handleSubmitComment}
        onCancel={handleCancelReply}
        loading={submitting}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.bg,
  },
  header: {
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
  },
  title: {
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.text,
  },
  sortButtons: {
    flexDirection: 'row',
  },
  sortButton: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
    marginLeft: theme.spacing.xs,
  },
  sortButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  sortButtonText: {
    fontSize: theme.fontSize.sm,
    color: theme.colors.textSecondary,
  },
  sortButtonTextActive: {
    color: 'white',
    fontWeight: theme.fontWeight.medium,
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.border,
    marginLeft: theme.spacing.lg + 40 + theme.spacing.md, // Avatar width + margin + padding
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: theme.spacing.xl * 2,
  },
  emptyTitle: {
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.text,
    marginBottom: theme.spacing.sm,
  },
  emptyText: {
    fontSize: theme.fontSize.base,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  footerLoader: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: theme.spacing.lg,
  },
  loadingText: {
    marginLeft: theme.spacing.sm,
    fontSize: theme.fontSize.sm,
    color: theme.colors.textSecondary,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
